import { FC } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import { FieldRow, FieldLabel, Panel, Title } from 'src/generic/component';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { Draft } from 'src/types/common';
import { IAIRequest, IStudentSchedule } from 'src/types/org/ai/schoolInterface';

interface IOnsiteInstructionPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const OnsiteInstructionPanel: FC<IOnsiteInstructionPanelProps> = ({
  item,
  readOnly,
  errors,
  onUpdate
}) => {
  const { getError, hasError } = ErrorManager(errors);

  const appForm = item.data?.appForm;
  const { instructionDays, daySchedule, hoursOnsite } = appForm?.studentSchedule || {};

  // All days for the time grid
  const ALL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const changed = (updates: Partial<IStudentSchedule>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        appForm: {
          ...appForm,
          studentSchedule: {
            ...appForm?.studentSchedule,
            ...updates
          }
        }
      }
    };
    onUpdate(updatedItem);
  };

  // Helper functions to convert between 12-hour and 24-hour format
  const convertTo24Hour = (time12h: string): string => {
    if (!time12h) return '';

    try {
      const match = time12h.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!match) return '';

      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const period = match[3].toUpperCase();

      if (period === 'AM' && hours === 12) {
        hours = 0;
      } else if (period === 'PM' && hours !== 12) {
        hours += 12;
      }

      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } catch {
      return '';
    }
  };

  const convertTo12Hour = (time24h: string): string => {
    if (!time24h) return '';

    try {
      const [hours, minutes] = time24h.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return '';
    }
  };

  const handleScheduleUpdate = (day: string, field: 'startTime' | 'endTime', time24h: string) => {
    const dayKey = day.toLowerCase();
    const time12h = convertTo12Hour(time24h);

    const updatedDaySchedule = {
      ...daySchedule,
      [dayKey]: {
        ...daySchedule?.[dayKey as keyof typeof daySchedule],
        [field]: time12h
      }
    };
    changed({ daySchedule: updatedDaySchedule });
  };

    const handleHoursOnsiteUpdate = (day: string, value: string) => {
    const dayKey = day.toLowerCase();
    
    let sanitizedValue = value.replace(/[^0-9.]/g, '');
    
    const parts = sanitizedValue.split('.');
    if (parts.length > 2) {
      // More than one decimal - keep first decimal only
      sanitizedValue = parts[0] + '.' + parts.slice(1).join('');
    }
  
    // Validate format: up to 2 digits before decimal, up to 2 after
    // Valid: "6", "6.5", "10", "10.25", "24", "24.75"
    // Invalid: "100", "6.123", "24.999"
    
    // Check digits before decimal
    if (parts[0] && parts[0].length > 2) {
      return; // Don't update if more than 2 digits before decimal
    }
    
    // Check digits after decimal
    if (parts[1] && parts[1].length > 2) {
      sanitizedValue = parts[0] + '.' + parts[1].slice(0, 2);
    }
  
    // Validate range (0-24 hours)
    if (sanitizedValue && sanitizedValue !== '.') {
      const numValue = parseFloat(sanitizedValue);
      if (!isNaN(numValue) && numValue > 24) {
        return; // Don't allow values over 24
      }
    }
  
    const updatedHoursOnsite = {
      ...hoursOnsite,
      [dayKey]: sanitizedValue
    };
    changed({ hoursOnsite: updatedHoursOnsite });
  };

  const isDaySelected = (day: string) => {
    return (instructionDays || []).some(
      selected => selected.toLowerCase() === day.toLowerCase()
    );
  };

  // Helper to check if a time input has an error (checks both field-specific and parent-level errors)
  const hasTimeInputError = (day: string, field: 'startTime' | 'endTime') => {
    const dayKey = day.toLowerCase();
    const fieldPath = `data.appForm.studentSchedule.daySchedule.${dayKey}.${field}`;
    
    // Check field-specific error
    if (hasError(fieldPath)) return true;
    
    // Check parent-level error (from schema refinement)
    const parentError = hasError('data.appForm.studentSchedule.daySchedule');
    if (parentError && isDaySelected(day)) {
      const value = daySchedule?.[dayKey as keyof typeof daySchedule]?.[field];
      if (!value || value === '') return true;
    }
    
    return false;
  };

  // Helper to check if hours onsite input has an error
  const hasHoursOnsiteError = (day: string) => {
    const dayKey = day.toLowerCase();
    const fieldPath = `data.appForm.studentSchedule.hoursOnsite.${dayKey}`;
    
    // Check field-specific error
    if (hasError(fieldPath)) return true;
    
    // Check parent-level error (from schema refinement)
    const parentError = hasError('data.appForm.studentSchedule.hoursOnsite');
    if (parentError && isDaySelected(day)) {
      const value = hoursOnsite?.[dayKey as keyof typeof hoursOnsite];
      if (!value || value === '') return true;
    }
    
    return false;
  };

  return (
    <Panel>
      <Title>Instruction Time Details</Title>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <FieldRow>
            <FieldLabel variant="body1" style={{ marginBottom: '16px' }}>
              Please indicate on which days instruction is provided to students
            </FieldLabel>
            {/* Error message with icon for instruction days */}
              {hasError('data.appForm.studentSchedule.instructionDays') && (
                <span className="cb-input-helper cb-validation-error" style={{ display: 'block', marginBottom: '12px' }}>
                  {getError('data.appForm.studentSchedule.instructionDays')}
                </span>
              )}

            {/* Days Header Row with checkboxes */}
            <Box sx={{ display: 'flex', marginBottom: '12px', gap: '8px', alignItems: 'flex-start' }}>
              <Box sx={{ minWidth: '80px' }}></Box>
              {ALL_DAYS.map((day) => (
                <Box key={day} sx={{
                  minWidth: '130px',
                  maxWidth: '130px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <Box sx={{ marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      checked={isDaySelected(day)}
                      disabled={readOnly}
                      onChange={(e) => {
                        const dayKey = day.toLowerCase();
                        let newDays = [...(instructionDays || [])];
                        if (e.target.checked) {
                          if (!newDays.some(d => d.toLowerCase() === dayKey)) {
                            newDays.push(dayKey);
                          }
                        } else {
                          newDays = newDays.filter(d => d.toLowerCase() !== dayKey);
                          const updatedDaySchedule = { ...daySchedule };
                          if (updatedDaySchedule[dayKey as keyof typeof daySchedule]) {
                            delete updatedDaySchedule[dayKey as keyof typeof daySchedule];
                          }
                          const updatedHoursOnsite = { ...hoursOnsite };
                          if (updatedHoursOnsite[dayKey as keyof typeof hoursOnsite]) {
                            delete updatedHoursOnsite[dayKey as keyof typeof hoursOnsite];
                          }
                          changed({
                            instructionDays: newDays,
                            daySchedule: updatedDaySchedule,
                            hoursOnsite: updatedHoursOnsite
                          });
                          return;
                        }
                        changed({ instructionDays: newDays });
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: readOnly ? 'default' : 'pointer'
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            <FieldLabel variant="body1" style={{ marginBottom: '16px', marginTop: '16px' }}>
              Please indicate the time instruction begins and ends for students on each day selected
            </FieldLabel>

            {/* Start Time Row */}
            <Box sx={{ display: 'flex', marginBottom: '16px', gap: '8px', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ minWidth: '80px', fontWeight: 'bold', paddingTop: '8px' }}>
                Start Time
              </Typography>
              {ALL_DAYS.map(day => (
                <Box key={`start-${day}`} sx={{ minWidth: '130px', maxWidth: '130px' }}>
                  {isDaySelected(day) ? (
                    <Box>
                      <input
                        type="time"
                        disabled={readOnly}
                        placeholder="hh:mm"
                        value={convertTo24Hour(daySchedule?.[day.toLowerCase() as keyof typeof daySchedule]?.startTime || '')}
                        onChange={(e) => handleScheduleUpdate(day, 'startTime', e.target.value)}
                        style={{
                          width: '130px',
                          height: '36px',
                          fontSize: '14px',
                          padding: '6px 8px',
                          textAlign: 'center',
                          borderRadius: '4px',
                          border: hasTimeInputError(day, 'startTime')
                            ? '2px solid #f44336'
                            : '1px solid #ccc',
                          outline: 'none',
                          backgroundColor: readOnly ? '#f5f5f5' : 'white',
                          cursor: readOnly ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      />
                      {hasTimeInputError(day, 'startTime') && (
                        <span className="cb-input-helper cb-validation-error" style={{ display: 'block', marginTop: '2px', fontSize: '10px' }}>
                          {getError(`data.appForm.studentSchedule.daySchedule.${day.toLowerCase()}.startTime`) || 'Required'}
                        </span>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ height: '36px' }} />
                  )}
                </Box>
              ))}
            </Box>

            {/* End Time Row */}
            <Box sx={{ display: 'flex', marginBottom: '16px', gap: '8px', alignItems: 'flex-start' }}>
              <Typography variant="body2" sx={{ minWidth: '80px', fontWeight: 'bold', paddingTop: '8px' }}>
                End Time
              </Typography>
              {ALL_DAYS.map(day => (
                <Box key={`end-${day}`} sx={{ minWidth: '130px', maxWidth: '130px' }}>
                  {isDaySelected(day) ? (
                    <Box>
                      <input
                        type="time"
                        disabled={readOnly}
                        placeholder="hh:mm"
                        value={convertTo24Hour(daySchedule?.[day.toLowerCase() as keyof typeof daySchedule]?.endTime || '')}
                        onChange={(e) => handleScheduleUpdate(day, 'endTime', e.target.value)}
                        style={{
                          width: '130px',
                          height: '36px',
                          fontSize: '14px',
                          padding: '6px 8px',
                          textAlign: 'center',
                          borderRadius: '4px',
                          border: hasTimeInputError(day, 'endTime')
                            ? '2px solid #f44336'
                            : '1px solid #ccc',
                          outline: 'none',
                          backgroundColor: readOnly ? '#f5f5f5' : 'white',
                          cursor: readOnly ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit'
                        }}
                      />
                      {hasTimeInputError(day, 'endTime') && (
                        <span className="cb-input-helper cb-validation-error" style={{ display: 'block', marginTop: '2px', fontSize: '10px' }}>
                          {getError(`data.appForm.studentSchedule.daySchedule.${day.toLowerCase()}.endTime`) || 'Required'}
                        </span>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ height: '36px' }} />
                  )}
                </Box>
              ))}
            </Box>

            {/* Hours On-site Section */}
            <FieldLabel variant="body1" style={{ marginBottom: '16px', marginTop: '16px' }}>
              Please indicate how many of these hours per day students are required to report <b>on-site</b> for instruction
            </FieldLabel>

            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Box sx={{ minWidth: '80px' }}></Box>
              {ALL_DAYS.map(day => (
                <Box key={`hours-${day}`} sx={{
                  minWidth: '130px',
                  maxWidth: '130px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  {isDaySelected(day) ? (
                    <>
                      <Typography variant="body2" sx={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}>
                        {day}
                      </Typography>
                      <input
                        type="text"
                        disabled={readOnly}
                        value={hoursOnsite?.[day.toLowerCase() as keyof typeof hoursOnsite] || ''}
                        placeholder=""
                        onChange={(e) => handleHoursOnsiteUpdate(day, e.target.value)}
                        style={{
                          width: '70px',
                          height: '50px',
                          fontSize: '14px',
                          padding: '4px',
                          textAlign: 'center',
                          marginBottom: '8px',
                          borderRadius: '4px',
                          border: hasHoursOnsiteError(day)
                            ? '2px solid #f44336'
                            : '1px solid #ccc',
                          outline: 'none'
                        }}
                      />
                      {hasHoursOnsiteError(day) && (
                        <span className="cb-input-helper cb-validation-error" style={{ display: 'block', marginBottom: '4px', textAlign: 'center', fontSize: '10px' }}>
                          {getError(`data.appForm.studentSchedule.hoursOnsite.${day.toLowerCase()}`) || 'Required'}
                        </span>
                      )}
                      <Typography variant="caption" sx={{
                        fontSize: '12px',
                        textAlign: 'center',
                        fontWeight: 'normal'
                      }}>
                        Hours On-site
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ height: '120px' }} />
                  )}
                </Box>
              ))}
            </Box>
          </FieldRow>
        </Grid>
      </Grid>
    </Panel>
  );
};